/**
 * Data Validation Connector configuration component.
 * Specialized configuration for Data Validation connector.
 */

// React Imports
import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  TextField,
} from '@mui/material';

export interface DataValidationConnectorConfigProps {
  settingsJson?: string;
  onChange?: (settingsJson: string) => void;
}

/**
 * Data Validation Connector configuration component.
 */
export const DataValidationConnectorConfig = ({
  settingsJson = '{}',
  onChange,
}: DataValidationConnectorConfigProps) => {
  const [service, setService] = useState<'nppes' | 'address' | 'cms'>('nppes');
  const [apiKey, setApiKey] = useState('');
  const [uspsUserId, setUspsUserId] = useState('');

  const handleServiceChange = (newService: 'nppes' | 'address' | 'cms') => {
    setService(newService);
    // Update settings JSON
    const settings: any = {};
    if (newService === 'nppes' || newService === 'cms') {
      settings.service = newService;
      settings.apiKey = apiKey;
    } else if (newService === 'address') {
      settings.service = 'address';
      settings.uspsUserId = uspsUserId;
    }
    onChange?.(JSON.stringify(settings));
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Service Configuration
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select Service
          </Typography>
          <ToggleButtonGroup
            value={service}
            exclusive
            onChange={(_, newService) => newService && handleServiceChange(newService)}
            fullWidth
          >
            <ToggleButton value="nppes">NPPES / NPI Registry</ToggleButton>
            <ToggleButton value="address">Address Validation (USPS)</ToggleButton>
            <ToggleButton value="cms">CMS Provider Data</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {service === 'nppes' || service === 'cms' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="API Key"
              type="password"
              value={apiKey}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setApiKey(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.apiKey = e.target.value;
                settings.service = service;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
              helperText={service === 'nppes' ? 'NPPES API key (if required)' : 'CMS API key'}
            />
            {service === 'nppes' && (
              <Alert severity="info">
                NPPES API is publicly available and may not require an API key. Some features may have rate limits.
              </Alert>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="USPS API User ID"
              value={uspsUserId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setUspsUserId(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.uspsUserId = e.target.value;
                settings.service = 'address';
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
              helperText="USPS Web Tools API User ID"
            />
            <Alert severity="info">
              USPS Address Validation API requires registration. Obtain your User ID from the USPS Web Tools website.
            </Alert>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
